import { z } from 'zod';

/**
 * Proposals: options offered to a customer before a sale, each made of items
 * written like a sale's services. Request schemas here; shapes in contract.ts.
 */

const id = z.uuid();
const version = z.number().int().positive();
const blankToNull = <T>(v: T) => (v === '' ? null : v);
const optional = (max: number) =>
  z.string().trim().max(max).nullable().optional().transform(blankToNull);
const day = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Tarihi seçin')
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

export const PROPOSAL_STATUSES = ['draft', 'sent', 'accepted', 'rejected'] as const;
export type ProposalStatus = (typeof PROPOSAL_STATUSES)[number];
/** Expired is derived: sent, and past its validity. */
export type ProposalState = ProposalStatus | 'expired';
export const PROPOSAL_STATE_LABELS: Record<ProposalState, string> = {
  draft: 'Taslak',
  sent: 'Gönderildi',
  accepted: 'Kabul edildi',
  rejected: 'Reddedildi',
  expired: 'Süresi doldu',
};

export const createProposalInput = z
  .object({
    accountId: id.nullable().optional(),
    contactId: id.nullable().optional(),
    /** The company it is made in; asked for when the member works in several (ADR 0017). */
    companyId: id.nullable().optional(),
    title: z.string().trim().min(1, 'Teklifin başlığını yazın').max(160),
    intro: optional(4000),
    validUntil: day,
    showItemPrices: z.boolean().default(true),
    notes: optional(4000),
    departmentId: id.nullable().optional(),
  })
  .refine((p) => p.accountId || p.contactId, {
    path: ['customer'],
    message: 'Bir firma ya da kişi seçin',
  });
export type CreateProposalInput = z.input<typeof createProposalInput>;

export const updateProposalInput = z
  .object({
    accountId: id.nullable(),
    contactId: id.nullable(),
    title: z.string().trim().min(1, 'Teklifin başlığını yazın').max(160),
    intro: optional(4000),
    validUntil: day,
    showItemPrices: z.boolean(),
    notes: optional(4000),
    ownerUserId: id,
  })
  .partial()
  .extend({ version });

/** What the customer reads under an item, sent beside the item's own fields (ADR 0018). */
export const proposalItemNoteInput = z.object({ customerNote: optional(2000) });
/** Photos per item, and the most one may weigh once the browser has made it smaller. */
export const QUOTE_PHOTO_LIMIT = 6;
export const QUOTE_PHOTO_MAX_BYTES = 2 * 1024 * 1024;

export const proposalOptionInput = z.object({
  title: z.string().trim().min(1, 'Seçeneğin adını yazın').max(120),
  description: optional(1000),
});

/** Moving through the life of a proposal; accepting names the option chosen. */
export const proposalDecisionInput = z.object({
  status: z.enum(PROPOSAL_STATUSES),
  optionId: id.nullable().optional(),
  note: optional(1000),
  version,
});

/** Turning the accepted option into a sale; travellers fill items without people. */
export const convertProposalInput = z
  .object({
    optionId: id,
    travellers: z.array(id).max(99).default([]),
    /** Where the bookings stand: confirmed, or held on option until a deadline. */
    status: z.enum(['option', 'confirmed']).default('option'),
    optionDeadline: z.iso.datetime({ offset: true }).nullable().optional(),
    version,
  })
  .refine((c) => c.status !== 'option' || c.optionDeadline, {
    path: ['optionDeadline'],
    message: 'Opsiyon son tarihini girin',
  });

export const proposalListQuery = z.object({
  q: z.string().trim().max(100).optional(),
  status: z.enum(['all', 'draft', 'sent', 'accepted', 'rejected', 'expired']).default('all'),
  mine: z.enum(['true', 'false']).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  offset: z.coerce.number().int().min(0).default(0),
});
